import type { RailTeam } from "../team-roster-rail";

/**
 * KAN-878: живые команды турнира → лента составов.
 *
 * Архив кормит ленту ЗАМОРОЖЕННЫМ снимком, живой турнир — текущим составом из
 * `/tournaments/{id}/teams`. Приводить их к одному виду можно и нужно: страница
 * должна выглядеть одинаково до и после архивации (KAN-787).
 *
 * НО ЭТО РАЗНЫЕ ФАКТЫ, И ПОДМЕНЯТЬ ИХ МОЛЧА НЕЛЬЗЯ. У архива состав — то, каким он
 * был на турнире; здесь — какой он прямо сейчас, и завтра он может стать другим.
 * Одинаковый вид не делает их одинаковыми по смыслу: это ровно та подмена, из-за
 * которой в KAN-808 замороженный твич отделили от живого, а ранг в архиве не стали
 * подставлять сегодняшним.
 *
 * Практическое следствие: поля, которых у живой ручки нет по существу (ранг на
 * момент заявки, твич на момент турнира), остаются пустыми, а НЕ заполняются
 * сегодняшними значениями.
 */

/** Команда турнира, как её отдаёт `/tournaments/{id}/teams`. */
export interface LiveTournamentTeam {
  id: string;
  name: string;
  tag?: string | null;
  icon?: string | null;
  logoUrl?: string | null;
  captainId?: string | null;
  members?: LiveTeamMember[] | null;
}

export interface LiveTeamMember {
  userId?: string | null;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  role?: string | null;
}

/** Место и счёт команды — из результатов турнира, они же кормят пьедестал. */
export interface LiveTeamPlacement {
  placement?: number | null;
  wins: number;
  losses: number;
}

export function liveTeamsToRail(
  teams: LiveTournamentTeam[] | null | undefined,
  placementByTeamId?: Record<string, LiveTeamPlacement | undefined>,
): RailTeam[] {
  return (teams ?? []).map((team) => {
    const place = placementByTeamId?.[team.id];
    const wins = place?.wins ?? 0;
    const losses = place?.losses ?? 0;
    const played = wins + losses;

    return {
      originalTeamId: team.id,
      name: team.name,
      tag: team.tag,
      icon: team.icon ?? team.logoUrl,
      captainId: team.captainId,
      placement: place?.placement ?? null,
      roster: (team.members ?? []).map((m) => ({
        userId: m.userId,
        displayName: m.displayName ?? m.username,
        avatarUrl: m.avatarUrl,
        role: m.role,
        // rank и twitchLogin намеренно не заполняются: у живой ручки их нет, а
        // подставить сегодняшние значения значило бы выдать «как сейчас» за «как
        // было на турнире». Лента и так рисует эти отметки только при наличии.
      })),
      // Счёт показываем, только если команда сыграла: «0 : 0» у ещё не начавшей
      // команды читается как «проиграла всё вничью», а не «матчей не было».
      stats: played > 0 ? { wins, losses, winrate: (wins / played) * 100 } : null,
    };
  });
}
